import { debounce } from "lodash-es";
import { onViewLoad } from "jose_modern/on_view_load";
import { Confirm } from "jose_modern/confirm";
import { updateAllergensIngredientAllergenPath } from "routes";
import { appendScript } from "../shared/requests";
import { IngredientFiltersMixin } from "../shared/ingredient_filters_mixin";
import { AllergenInput, initToggles, postAllergens } from "./common.js";

class AllergensForUser {
  constructor(container) {
    this.maybeNavigate = debounce(this.navigate, 200, { maxWait: 1000 });

    this.container = container;
    history.replaceState(window.location.href, "", window.location.href);

    this.parseUrl(window.location.href);
    this.bind();
  }

  parseUrl(url) {
    const parsed = new URL(url);
    this.q = parsed.searchParams.get("q") || "";
    this.page = parsed.searchParams.get("page") || 1;
    this.archived = parsed.searchParams.get("archived") === "true";
    this.parseTagsFromUrl(parsed);
  }

  bind() {
    this.bindSearch();
    this.bindTags();
    this.bindResetFilters();
    this.bindAllergenGrid();
    this.bindUnlockButton();
    this.maybeShowAdvanced();

    document.addEventListener("partial:replace", (e) => {
      if (e.partial === "ingredient_allergens/allergen_edit_table") {
        this.bindAllergenGrid();
        this.bindTags();
      }
    });

    window.addEventListener("popstate", async (e) => {
      this.parseUrl(e.state);

      this.container.querySelector(".ingredient-search").value = this.q;
      this.setSearchFilterTags(this.tags);
      this.container.querySelector("#ingredient-allergen-archived-filter").checked = this.archived;
      this.maybeShowAdvanced();

      this.navigate(true);
    });
  }

  bindSearch() {
    const search = this.container.querySelector(".ingredient-search");
    search.addEventListener("input", async (e) => {
      this.q = e.target.value;
      this.page = 1;

      this.maybeNavigate();
    });

    this.bindTagSearchFilter();

    const archive = this.container.querySelector("#ingredient-allergen-archived-filter");
    archive.addEventListener("change", async (e) => {
      this.archived = e.target.checked;
      this.page = 1;

      this.navigate();
    });
  }

  showAdvanced() {
    document.querySelector("#ingredient-allergen-advanced-filters").classList.remove("is-hidden");
  }

  bindAllergenGrid() {
    this.container.querySelectorAll(".ingredient-allergens").forEach((container) => {
      AllergenInput.bindAutosave(container, (allergens) => {
        const { ingredientAllergenId } = container.dataset;

        updateAllergensOnIngredient(ingredientAllergenId, allergens);
      });
    });
  }

  bindUnlockButton() {
    this.container
      .querySelector("#unlock_all_recipe_allergens")
      .addEventListener("click", async (e) => {
        e.preventDefault();
        const confirmed = await Confirm.confirm(
          `This will disable manual allergen editing on all recipes, and the
          allergens for those  recipes will be set based on their
          ingredient-level allergens.
          <br>
          <br>
          Updating ingredient-level allergens on this or other allergen pages
          will update the allergens for all recipes that use the ingredient.
          <br>
          <br>
          Do you wish to continue?`,
          { confirm: "Yes, use ingredient allergens on recipes" }
        );

        if (confirmed) {
          e.target.form.submit();
        }
      });
  }

  resetFilters() {
    this.q = "";
    this.archived = false;
    this.tags = [];
    this.page = 1;

    this.container.querySelector(".ingredient-search").value = "";
    this.container.querySelector("#ingredient-allergen-archived-filter").checked = false;
    this.setSearchFilterTags(this.tags);

    this.navigate();
  }

  async navigate(skipHistory = false) {
    const url = new URL(window.location.href);
    url.searchParams.set("q", this.q);
    url.searchParams.set("page", this.page);
    url.searchParams.set("archived", this.archived);

    this.appendTagsToUrl(url);

    if (!skipHistory) {
      history.pushState(url.href, "", url.href);
    }

    this.updateResetFiltersUI();

    const response = await fetch(url, {
      headers: {
        Accept: "application/javascript",
      },
    });
    const data = await response.text();

    appendScript(data);
  }
}

Object.assign(AllergensForUser.prototype, IngredientFiltersMixin);

async function updateAllergensOnIngredient(ingredientAllergenId, allergens) {
  const url = updateAllergensIngredientAllergenPath(ingredientAllergenId);

  await postAllergens(url, allergens);
}

function initSearchPage() {
  document.querySelectorAll("html").forEach((page) => {
    new AllergensForUser(page);
  });
}

onViewLoad("ingredient_allergens#index_for_user", () => {
  initSearchPage();
  initToggles();
});
